import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  deleteUser,
  getAllUsersWithClub,
  updateCoachBadgePrinted,
  updateCoachBadgeReceived,
  updateUser,
} from "../../../../../services/userServices";

import { getAllValidationBadges } from "../../../../../services/validationBadgeServices";

import {
  getClubs,
  linkUserToClub,
} from "../../../../../services/clubServices";

import { sendMessage } from "../../../../../services/messageServices";

import { useNotification } from "../../../../../components/Notification/NotificationManager";

import { useUser } from "./../../../../Home/UserContext";

import {
  PAGE_SIZE,
  ROLE_OPTIONS,
  ROLES_IN_CLUB,
} from "../data/usersAdmin.data";

import {
  addClubToUserLocally,
  extractActeurs,
  extractClubs,
  getCreatedTime,
  groupBadgesByUser,
  normalize,
} from "../utils/usersAdmin.utils";

import { exportUsersToExcel } from "../utils/usersExport.utils";
import { generateCoachBadgePng } from "../utils/coachBadgeExport.utils";

import useDebouncedValue from "./useDebouncedValue";

export default function useUsersAdmin() {
  const { addNotification } = useNotification();
  const { user: connectedUser } = useUser();

  /* -------------------------------------------------------------------------- */
  /*                                  Données                                   */
  /* -------------------------------------------------------------------------- */

  const [loading, setLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [badgesByUser, setBadgesByUser] = useState({});
  const [allClubs, setAllClubs] = useState([]);

  /* -------------------------------------------------------------------------- */
  /*                                  Filtres                                   */
  /* -------------------------------------------------------------------------- */

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [recentFilter, setRecentFilter] = useState("");

  const [coachBadgePrintedFilter, setCoachBadgePrintedFilter] = useState("");
  const [coachBadgeReceivedFilter, setCoachBadgeReceivedFilter] = useState("");

  const [sortAsc, setSortAsc] = useState(true);

  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  /* -------------------------------------------------------------------------- */
  /*                                Pagination                                  */
  /* -------------------------------------------------------------------------- */

  const [page, setPage] = useState(1);

  /* -------------------------------------------------------------------------- */
  /*                              Modale utilisateur                            */
  /* -------------------------------------------------------------------------- */

  const [isUserModalOpen, setUserModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  /* -------------------------------------------------------------------------- */
  /*                              Liaison au club                               */
  /* -------------------------------------------------------------------------- */

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkTargetUser, setLinkTargetUser] = useState(null);

  const [linkDepartement, setLinkDepartement] = useState("");
  const [linkSearch, setLinkSearch] = useState("");

  const [linkSelectedClubId, setLinkSelectedClubId] = useState("");
  const [linkRoleInClub, setLinkRoleInClub] = useState(ROLES_IN_CLUB[0]);

  const [linking, setLinking] = useState(false);

  /* -------------------------------------------------------------------------- */
  /*                                Messagerie                                  */
  /* -------------------------------------------------------------------------- */

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageTargetUser, setMessageTargetUser] = useState(null);

  const [messageSubject, setMessageSubject] = useState("");
  const [messageContent, setMessageContent] = useState("");

  const [sendingMessage, setSendingMessage] = useState(false);

  /* -------------------------------------------------------------------------- */
  /*                                  Export                                    */
  /* -------------------------------------------------------------------------- */

  const [exportingExcel, setExportingExcel] = useState(false);

  const [
    exportingCoachBadgeUserId,
    setExportingCoachBadgeUserId,
  ] = useState(null);

  /* -------------------------------------------------------------------------- */
  /*                       Mise à jour des statuts badge                        */
  /* -------------------------------------------------------------------------- */

  const [coachBadgeUpdatingByUser, setCoachBadgeUpdatingByUser] =
    useState({});

  /* -------------------------------------------------------------------------- */
  /*                              Chargement initial                            */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const [
          usersResponse,
          badgesResponse,
          clubsResponse,
        ] = await Promise.all([
          getAllUsersWithClub(),
          getAllValidationBadges(),
          getClubs(),
        ]);

        setUsers(
          Array.isArray(usersResponse)
            ? usersResponse
            : [],
        );

        setBadgesByUser(
          groupBadgesByUser(
            Array.isArray(badgesResponse)
              ? badgesResponse
              : [],
          ),
        );

        setAllClubs(
          Array.isArray(clubsResponse)
            ? clubsResponse
            : [],
        );
      } catch (error) {
        console.error(
          "Erreur chargement utilisateurs :",
          error,
        );

        addNotification(
          "Erreur lors du chargement des utilisateurs.",
          "error",
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [addNotification]);

  /* -------------------------------------------------------------------------- */
  /*                               Départements                                 */
  /* -------------------------------------------------------------------------- */

  const departements = useMemo(() => {
    const values = allClubs
      .map((club) => club?.departement_club)
      .filter(Boolean);

    return [...new Set(values)].sort((first, second) =>
      String(first).localeCompare(String(second), "fr"),
    );
  }, [allClubs]);

  /* -------------------------------------------------------------------------- */
  /*                            Clubs de la modale                              */
  /* -------------------------------------------------------------------------- */

  const visibleClubs = useMemo(() => {
    const clubs = linkDepartement
      ? allClubs.filter(
          (club) =>
            club?.departement_club === linkDepartement,
        )
      : allClubs;

    const query = normalize(linkSearch);

    return clubs
      .filter((club) =>
        query
          ? normalize(club?.nom_club).includes(query)
          : true,
      )
      .sort((first, second) =>
        String(first?.nom_club || "").localeCompare(
          String(second?.nom_club || ""),
          "fr",
          {
            sensitivity: "base",
          },
        ),
      );
  }, [
    allClubs,
    linkDepartement,
    linkSearch,
  ]);

  /* -------------------------------------------------------------------------- */
  /*                                  Filtres                                   */
  /* -------------------------------------------------------------------------- */

  const filteredUsers = useMemo(() => {
    let rows = [...users];

    const query = normalize(debouncedSearch);

    /* -------------------------------- Recherche ------------------------------- */

    if (query) {
      rows = rows.filter((currentUser) => {
        const acteursText = extractActeurs(currentUser)
          .map(
            (acteur) =>
              `${acteur?.nom || ""} ${acteur?.prenom || ""}`,
          )
          .join(" ");

        const clubsText = extractClubs(currentUser)
          .map(
            (club) =>
              club?.nom_club ||
              club?.name ||
              "",
          )
          .join(" ");

        const searchableText = normalize(
          [
            currentUser?.nom,
            currentUser?.prenom,
            currentUser?.email,
            acteursText,
            clubsText,
          ].join(" "),
        );

        return searchableText.includes(query);
      });
    }

    /* ---------------------------------- Rôle ---------------------------------- */

    if (selectedRole !== "") {
      const roleId = Number(selectedRole);

      rows = rows.filter(
        (currentUser) =>
          Number(currentUser?.role_id) === roleId,
      );
    }

    /* ------------------------------ Badge imprimé ----------------------------- */

    if (coachBadgePrintedFilter !== "") {
      const expectedValue =
        coachBadgePrintedFilter === "true";

      rows = rows.filter(
        (currentUser) =>
          Boolean(currentUser?.coach_badge_printed) ===
          expectedValue,
      );
    }

    /* ---------------------------- Badge réceptionné --------------------------- */

    if (coachBadgeReceivedFilter !== "") {
      const expectedValue =
        coachBadgeReceivedFilter === "true";

      rows = rows.filter(
        (currentUser) =>
          Boolean(currentUser?.coach_badge_received) ===
          expectedValue,
      );
    }

    /* ----------------------------- Date création ------------------------------ */

    if (recentFilter) {
      const now = Date.now();

      let cutoff = 0;

      if (recentFilter === "year") {
        const firstDayOfYear = new Date();

        firstDayOfYear.setMonth(0, 1);
        firstDayOfYear.setHours(0, 0, 0, 0);

        cutoff = firstDayOfYear.getTime();
      } else {
        const days = Number(recentFilter);

        cutoff =
          now -
          days * 24 * 60 * 60 * 1000;
      }

      rows = rows.filter(
        (currentUser) =>
          getCreatedTime(currentUser) >= cutoff,
      );
    }

    /* ---------------------------------- Tri ----------------------------------- */

    rows.sort((firstUser, secondUser) => {
      const firstName = normalize(
        `${firstUser?.nom || ""} ${firstUser?.prenom || ""}`,
      );

      const secondName = normalize(
        `${secondUser?.nom || ""} ${secondUser?.prenom || ""}`,
      );

      return sortAsc
        ? firstName.localeCompare(secondName, "fr")
        : secondName.localeCompare(firstName, "fr");
    });

    return rows;
  }, [
    users,
    debouncedSearch,
    selectedRole,
    recentFilter,
    coachBadgePrintedFilter,
    coachBadgeReceivedFilter,
    sortAsc,
  ]);

  /* -------------------------------------------------------------------------- */
  /*                       Retour page 1 après un filtre                        */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    selectedRole,
    recentFilter,
    coachBadgePrintedFilter,
    coachBadgeReceivedFilter,
    sortAsc,
  ]);

  /* -------------------------------------------------------------------------- */
  /*                                Statistiques                                */
  /* -------------------------------------------------------------------------- */

  const totalUsers = filteredUsers.length;

  const totalBadges = useMemo(
    () =>
      filteredUsers.reduce(
        (total, currentUser) =>
          total +
          (badgesByUser[currentUser.id]?.length || 0),
        0,
      ),
    [
      filteredUsers,
      badgesByUser,
    ],
  );

  const totalLinkedClubs = useMemo(() => {
    const clubIds = new Set();

    filteredUsers.forEach((currentUser) => {
      extractClubs(currentUser).forEach((club) => {
        if (club?.id) {
          clubIds.add(club.id);
        }
      });
    });

    return clubIds.size;
  }, [filteredUsers]);

  const totalActeurs = useMemo(
    () =>
      filteredUsers.reduce(
        (total, currentUser) =>
          total + extractActeurs(currentUser).length,
        0,
      ),
    [filteredUsers],
  );

  /* -------------------------------------------------------------------------- */
  /*                                Pagination                                  */
  /* -------------------------------------------------------------------------- */

  const pageCount = Math.max(
    1,
    Math.ceil(filteredUsers.length / PAGE_SIZE),
  );

  const safePage = Math.min(page, pageCount);

  const paginatedRows = useMemo(() => {
    const startIndex = (safePage - 1) * PAGE_SIZE;

    return filteredUsers.slice(
      startIndex,
      startIndex + PAGE_SIZE,
    );
  }, [
    filteredUsers,
    safePage,
  ]);

  /* -------------------------------------------------------------------------- */
  /*                           Modification utilisateur                         */
  /* -------------------------------------------------------------------------- */

  const openEditModal = (selectedUser) => {
    setCurrentUser(selectedUser);
    setUserModalOpen(true);
  };

  const closeEditModal = () => {
    setUserModalOpen(false);
    setCurrentUser(null);
  };

  const submitUserUpdate = async (updatedFields) => {
    if (!currentUser?.id) return;

    try {
      const token = localStorage.getItem("token");

      await updateUser(
        currentUser.id,
        updatedFields,
        token,
      );

      setUsers((previousUsers) =>
        previousUsers.map((existingUser) =>
          existingUser.id === currentUser.id
            ? {
                ...existingUser,
                ...updatedFields,
              }
            : existingUser,
        ),
      );

      addNotification(
        "Utilisateur mis à jour.",
        "success",
      );

      closeEditModal();
    } catch (error) {
      console.error(
        "Erreur mise à jour utilisateur :",
        error,
      );

      addNotification(
        "Erreur lors de la mise à jour.",
        "error",
      );
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                          Suppression utilisateur                           */
  /* -------------------------------------------------------------------------- */

  const removeUser = async (userId) => {
    const confirmed = window.confirm(
      "Supprimer définitivement cet utilisateur ?",
    );

    if (!confirmed) return;

    try {
      await deleteUser(userId);

      setUsers((previousUsers) =>
        previousUsers.filter(
          (currentUser) =>
            currentUser.id !== userId,
        ),
      );

      setBadgesByUser((previousBadges) => {
        const nextBadges = {
          ...previousBadges,
        };

        delete nextBadges[userId];

        return nextBadges;
      });

      addNotification(
        "Utilisateur supprimé.",
        "success",
      );
    } catch (error) {
      console.error(
        "Erreur suppression utilisateur :",
        error,
      );

      addNotification(
        "Erreur lors de la suppression.",
        "error",
      );
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                           Badge Coach - imprimé                            */
  /* -------------------------------------------------------------------------- */

  const toggleCoachBadgePrinted = async (selectedUser) => {
    if (!selectedUser?.id) return;

    const userId = selectedUser.id;

    const nextValue =
      !Boolean(selectedUser.coach_badge_printed);

    try {
      setCoachBadgeUpdatingByUser((previous) => ({
        ...previous,
        [userId]: "printed",
      }));

      const response = await updateCoachBadgePrinted(
        userId,
        nextValue,
      );

      const updatedUser = response?.user;

      setUsers((previousUsers) =>
        previousUsers.map((currentUser) => {
          if (
            Number(currentUser.id) !==
            Number(userId)
          ) {
            return currentUser;
          }

          return {
            ...currentUser,

            coach_badge_printed:
              updatedUser?.coach_badge_printed !== undefined
                ? Boolean(
                    updatedUser.coach_badge_printed,
                  )
                : nextValue,

            coach_badge_received:
              updatedUser?.coach_badge_received !== undefined
                ? Boolean(
                    updatedUser.coach_badge_received,
                  )
                : nextValue
                  ? Boolean(
                      currentUser.coach_badge_received,
                    )
                  : false,
          };
        }),
      );

      addNotification(
        response?.message ||
          (nextValue
            ? "Badge marqué comme imprimé."
            : "Badge marqué comme non imprimé."),
        "success",
      );
    } catch (error) {
      console.error(
        "Erreur statut badge imprimé :",
        error,
      );

      addNotification(
        error?.response?.data?.message ||
          "Impossible de modifier le statut d'impression.",
        "error",
      );
    } finally {
      setCoachBadgeUpdatingByUser((previous) => {
        const next = {
          ...previous,
        };

        delete next[userId];

        return next;
      });
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                         Badge Coach - réceptionné                          */
  /* -------------------------------------------------------------------------- */

  const toggleCoachBadgeReceived = async (selectedUser) => {
    if (!selectedUser?.id) return;

    if (!Boolean(selectedUser.coach_badge_printed)) {
      addNotification(
        "Le badge doit d'abord être marqué comme imprimé.",
        "error",
      );

      return;
    }

    const userId = selectedUser.id;

    const nextValue =
      !Boolean(selectedUser.coach_badge_received);

    try {
      setCoachBadgeUpdatingByUser((previous) => ({
        ...previous,
        [userId]: "received",
      }));

      const response = await updateCoachBadgeReceived(
        userId,
        nextValue,
      );

      const updatedUser = response?.user;

      setUsers((previousUsers) =>
        previousUsers.map((currentUser) => {
          if (
            Number(currentUser.id) !==
            Number(userId)
          ) {
            return currentUser;
          }

          return {
            ...currentUser,

            coach_badge_printed:
              updatedUser?.coach_badge_printed !== undefined
                ? Boolean(
                    updatedUser.coach_badge_printed,
                  )
                : Boolean(
                    currentUser.coach_badge_printed,
                  ),

            coach_badge_received:
              updatedUser?.coach_badge_received !== undefined
                ? Boolean(
                    updatedUser.coach_badge_received,
                  )
                : nextValue,
          };
        }),
      );

      addNotification(
        response?.message ||
          (nextValue
            ? "Badge marqué comme réceptionné."
            : "Badge marqué comme non réceptionné."),
        "success",
      );
    } catch (error) {
      console.error(
        "Erreur statut badge réceptionné :",
        error,
      );

      addNotification(
        error?.response?.data?.message ||
          "Impossible de modifier le statut de réception.",
        "error",
      );
    } finally {
      setCoachBadgeUpdatingByUser((previous) => {
        const next = {
          ...previous,
        };

        delete next[userId];

        return next;
      });
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                Liaison club                                */
  /* -------------------------------------------------------------------------- */

  const openLinkClubModal = (selectedUser) => {
    setLinkTargetUser(selectedUser);
    setLinkDepartement("");
    setLinkSearch("");
    setLinkSelectedClubId("");
    setLinkRoleInClub(ROLES_IN_CLUB[0]);
    setLinkModalOpen(true);
  };

  const closeLinkClubModal = () => {
    setLinkModalOpen(false);
    setLinkTargetUser(null);
  };

  const submitClubLink = async (event) => {
    event?.preventDefault?.();

    if (!linkTargetUser?.id) return;

    if (!linkSelectedClubId) {
      addNotification(
        "Sélectionnez un club.",
        "error",
      );

      return;
    }

    try {
      setLinking(true);

      await linkUserToClub(
        Number(linkSelectedClubId),
        {
          user_id: linkTargetUser.id,
          role_in_club: linkRoleInClub,
        },
      );

      const selectedClub = allClubs.find(
        (club) =>
          String(club.id) ===
          String(linkSelectedClubId),
      );

      if (selectedClub) {
        setUsers((previousUsers) =>
          previousUsers.map((existingUser) =>
            existingUser.id === linkTargetUser.id
              ? addClubToUserLocally(
                  existingUser,
                  selectedClub,
                )
              : existingUser,
          ),
        );
      }

      addNotification(
        "Utilisateur lié au club avec succès.",
        "success",
      );

      closeLinkClubModal();
    } catch (error) {
      console.error(
        "Erreur liaison club :",
        error,
      );

      addNotification(
        "Impossible de lier cet utilisateur au club.",
        "error",
      );
    } finally {
      setLinking(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                 Message                                    */
  /* -------------------------------------------------------------------------- */

  const openMessageModal = (selectedUser) => {
    setMessageTargetUser(selectedUser);
    setMessageSubject("");
    setMessageContent("");
    setMessageModalOpen(true);
  };

  const closeMessageModal = () => {
    setMessageModalOpen(false);
    setMessageTargetUser(null);
    setMessageSubject("");
    setMessageContent("");
  };

  const submitMessage = async (event) => {
    event?.preventDefault?.();

    const senderId =
      connectedUser?.userId ||
      connectedUser?.id;

    const recipientId =
      messageTargetUser?.id;

    if (!senderId || !recipientId) {
      addNotification(
        "Expéditeur ou destinataire introuvable.",
        "error",
      );

      return;
    }

    if (
      !messageSubject.trim() ||
      !messageContent.trim()
    ) {
      addNotification(
        "Le sujet et le contenu sont obligatoires.",
        "error",
      );

      return;
    }

    try {
      setSendingMessage(true);

      await sendMessage({
        sender_id: senderId,
        recipient_id: recipientId,
        subject: messageSubject.trim(),
        content: messageContent.trim(),
      });

      addNotification(
        "Message envoyé avec succès.",
        "success",
      );

      closeMessageModal();
    } catch (error) {
      console.error(
        "Erreur envoi message :",
        error,
      );

      addNotification(
        "Erreur lors de l'envoi du message.",
        "error",
      );
    } finally {
      setSendingMessage(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                  Filtres                                   */
  /* -------------------------------------------------------------------------- */

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedRole("");
    setRecentFilter("");
    setCoachBadgePrintedFilter("");
    setCoachBadgeReceivedFilter("");
    setSortAsc(true);
    setPage(1);
  };

  /* -------------------------------------------------------------------------- */
  /*                               Export Excel                                 */
  /* -------------------------------------------------------------------------- */

  const exportExcel = async () => {
    if (!filteredUsers.length) {
      addNotification(
        "Aucun utilisateur à exporter.",
        "error",
      );

      return;
    }

    try {
      setExportingExcel(true);

      await exportUsersToExcel({
        users: filteredUsers,
        badgesByUser,
        selectedRole,
        roleOptions: ROLE_OPTIONS,
      });

      addNotification(
        "Export Excel généré avec succès.",
        "success",
      );
    } catch (error) {
      console.error(
        "Erreur export Excel :",
        error,
      );

      addNotification(
        "Erreur lors de l'export Excel.",
        "error",
      );
    } finally {
      setExportingExcel(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                           Génération carte coach                           */
  /* -------------------------------------------------------------------------- */

  const exportCoachBadge = async (selectedUser) => {
    if (!selectedUser?.id) return;

    try {
      setExportingCoachBadgeUserId(
        selectedUser.id,
      );

      await generateCoachBadgePng(
        selectedUser,
      );

      addNotification(
        "Carte coach générée avec succès.",
        "success",
      );
    } catch (error) {
      console.error(
        "Erreur génération carte coach :",
        error,
      );

      addNotification(
        "Impossible de générer la carte coach.",
        "error",
      );
    } finally {
      setExportingCoachBadgeUserId(null);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                  Return                                    */
  /* -------------------------------------------------------------------------- */

  return {
    loading,

    rows: paginatedRows,
    filteredUsers,
    badgesByUser,

    roleOptions: ROLE_OPTIONS,

    stats: {
      totalUsers,
      totalBadges,
      totalLinkedClubs,
      totalActeurs,
    },

    filters: {
      searchTerm,
      setSearchTerm,

      selectedRole,
      setSelectedRole,

      recentFilter,
      setRecentFilter,

      coachBadgePrintedFilter,
      setCoachBadgePrintedFilter,

      coachBadgeReceivedFilter,
      setCoachBadgeReceivedFilter,

      sortAsc,

      toggleSort: () =>
        setSortAsc((current) => !current),

      resetFilters,

      debouncedSearch,
    },

    pagination: {
      page: safePage,
      pageCount,
      setPage,
    },

    editModal: {
      isOpen: isUserModalOpen,
      currentUser,
      open: openEditModal,
      close: closeEditModal,
      submit: submitUserUpdate,
    },

    linkClubModal: {
      isOpen: linkModalOpen,
      targetUser: linkTargetUser,

      departements,
      visibleClubs,

      linkDepartement,
      setLinkDepartement,

      linkSearch,
      setLinkSearch,

      selectedClubId: linkSelectedClubId,
      setSelectedClubId:
        setLinkSelectedClubId,

      roleInClub: linkRoleInClub,
      setRoleInClub: setLinkRoleInClub,

      linking,

      open: openLinkClubModal,
      close: closeLinkClubModal,
      submit: submitClubLink,
    },

    messageModal: {
      isOpen: messageModalOpen,
      targetUser: messageTargetUser,

      subject: messageSubject,
      setSubject: setMessageSubject,

      content: messageContent,
      setContent: setMessageContent,

      sending: sendingMessage,

      open: openMessageModal,
      close: closeMessageModal,
      submit: submitMessage,
    },

    actions: {
      removeUser,

      exportExcel,
      exportingExcel,

      exportCoachBadge,
      exportingCoachBadgeUserId,

      toggleCoachBadgePrinted,
      toggleCoachBadgeReceived,

      coachBadgeUpdatingByUser,
    },
  };
}